import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faWrench } from "@fortawesome/free-solid-svg-icons";
import { useDevTools } from "./DevToolsProvider";
import type { DevToolsPlugin, PluginStatus } from "./types";

function statusClass(state?: PluginStatus["state"]): string {
  if (!state) return "";
  return `dt-icon-btn--status-${state}`;
}

function badgeClass(state: PluginStatus["state"]): string {
  if (state === "warning") return "dt-badge dt-badge--warning";
  if (state === "error") return "dt-badge dt-badge--error";
  return "dt-badge";
}

const STATUS_PRIORITY: Record<string, number> = {
  error: 4,
  warning: 3,
  active: 2,
  success: 1,
  loading: 0,
  inactive: -1,
};

// Each plugin icon is its own component so useStatus (which may contain hooks)
// is called at the top level of a component, not inside a loop.
function PluginIconButton({
  plugin,
  isSelected,
  isDrawerOpen,
  onSelect,
  onStatusReport,
}: {
  plugin: DevToolsPlugin;
  isSelected: boolean;
  isDrawerOpen: boolean;
  onSelect: () => void;
  onStatusReport: (status: PluginStatus | undefined) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const status: PluginStatus | undefined = plugin.useStatus?.();
  // Called during render — the parent must collect into a ref, not setState,
  // or React will throw "Cannot update a component while rendering a different component".
  onStatusReport(status);

  const showSelected = isSelected && isDrawerOpen;
  const shouldPulse =
    !showSelected &&
    (status?.state === "active" || status?.state === "warning") &&
    status?.badge != null;
  const btnClass = [
    "dt-icon-btn",
    showSelected ? "dt-icon-btn--active" : statusClass(status?.state),
    shouldPulse ? "dt-icon-btn--pulsing" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      className={btnClass}
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={plugin.name}
      type="button"
    >
      <FontAwesomeIcon icon={plugin.icon} />
      {status && status.state !== "inactive" && status.badge != null && (
        <span className={badgeClass(status.state)}>{status.badge}</span>
      )}
      {hovered && (
        <span className="dt-tooltip">
          {plugin.name}
          {status?.summary ? ` - ${status.summary}` : ""}
        </span>
      )}
    </button>
  );
}

export function DevToolsSidebar() {
  const {
    plugins,
    activePluginId,
    isDrawerOpen,
    isSidebarExpanded,
    setActivePlugin,
    toggleSidebar,
  } = useDevTools();

  // Collect statuses reported by each PluginIconButton for FAB aggregation.
  // This is populated synchronously during render via onStatusReport callbacks.
  const statusMap = React.useRef<Map<string, PluginStatus | undefined>>(new Map());

  // Compute aggregated status from collected statuses
  let worstState: PluginStatus["state"] = "inactive";
  let worstPriority = -1;
  let issueCount = 0;
  for (const status of statusMap.current.values()) {
    if (!status) continue;
    const priority = STATUS_PRIORITY[status.state] ?? -1;
    if (priority > worstPriority) {
      worstPriority = priority;
      worstState = status.state;
    }
    if (status.state === "error" || status.state === "warning") {
      issueCount++;
    }
  }

  return (
    <>
      {/* FAB — always rendered, hidden when sidebar expanded */}
      <button
        className={[
          "dt-fab",
          isSidebarExpanded ? "dt-fab--hidden" : "",
          worstState === "error" || worstState === "warning"
            ? statusClass(worstState)
            : "",
          worstState === "error" || worstState === "warning"
            ? "dt-icon-btn--pulsing"
            : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={toggleSidebar}
        title="Open DevTools"
        type="button"
      >
        <FontAwesomeIcon icon={faWrench} />
        {issueCount > 0 && (
          <span className={badgeClass(worstState)}>{issueCount}</span>
        )}
      </button>

      {/* Sidebar — always rendered, slides off-screen when collapsed */}
      <div
        className={[
          "dt-sidebar",
          isSidebarExpanded ? "" : "dt-sidebar--collapsed",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {plugins.map((plugin) => (
          <PluginIconButton
            key={plugin.id}
            plugin={plugin}
            isSelected={activePluginId === plugin.id}
            isDrawerOpen={isDrawerOpen}
            onSelect={() => setActivePlugin(plugin.id)}
            onStatusReport={(status) => statusMap.current.set(plugin.id, status)}
          />
        ))}
        <div className="dt-sidebar__spacer" />
        <button
          className="dt-icon-btn"
          onClick={toggleSidebar}
          title="Collapse"
          type="button"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
      </div>
    </>
  );
}
